import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { CheckCircle2, FileText, User, Package, CreditCard, PenTool, Loader2, Download, AlertCircle, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { translations, formatCurrency } from '@/lib/translations';
import SignatureCanvas from 'react-signature-canvas';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerSigningPage = () => {
  const { contractId } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signResult, setSignResult] = useState(null);
  const [signatureLocation, setSignatureLocation] = useState('');
  const [termsExpanded, setTermsExpanded] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const sigCanvasRef = useRef(null);

  const fetchContract = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/contract/${contractId}/signing-data`);
      if (!res.ok) {
        if (res.status === 404) throw new Error('not_found');
        throw new Error('fetch_failed');
      }
      const data = await res.json();
      setContract(data);
      if (data.status === 'signed') {
        setSigned(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  const language = contract?.language || 'de';
  const t = translations[language];
  const locale = language === 'de' ? 'de-DE' : 'en-US';

  const handleSign = async () => {
    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
      alert(language === 'de' ? 'Bitte zeichnen Sie Ihre Unterschrift' : 'Please draw your signature');
      return;
    }
    if (!signatureLocation.trim()) {
      alert(language === 'de' ? 'Bitte geben Sie den Ort ein' : 'Please enter the location');
      return;
    }
    if (!termsAccepted) {
      alert(language === 'de' ? 'Bitte akzeptieren Sie die AGB' : 'Please accept the terms and conditions');
      return;
    }

    setSigning(true);
    try {
      const signatureDataUrl = sigCanvasRef.current.toDataURL('image/png');
      const today = new Date().toISOString().split('T')[0];

      const res = await fetch(`${API_URL}/api/contract/${contractId}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: contract.customer_info.name,
          companyName: contract.customer_info.companyName || null,
          location: signatureLocation,
          date: today,
          signatureDataUrl: signatureDataUrl,
          confirmationEmail: contract.customer_info.email,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Signing failed');
      }

      const result = await res.json();
      setSignResult(result);
      setSigned(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setSigning(false);
    }
  };

  const handleClearSignature = () => {
    if (sigCanvasRef.current) sigCanvasRef.current.clear();
  };

  const handleDownloadPdf = () => {
    window.open(`${API_URL}/api/contract/${contractId}/pdf`, '_blank');
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading contract...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-0 shadow-xl">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <AlertCircle className="w-16 h-16 text-destructive mx-auto" />
            <h2 className="text-xl font-semibold">
              {error === 'not_found' ? 'Vertrag nicht gefunden' : 'Fehler beim Laden'}
            </h2>
            <p className="text-muted-foreground">
              {error === 'not_found'
                ? 'Dieser Vertragslink ist ungültig oder abgelaufen.'
                : 'Der Vertrag konnte nicht geladen werden. Bitte versuchen Sie es später erneut.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const customerInfo = contract.customer_info || {};
  const productDetails = contract.product_details || {};
  const usePaymentPlan = contract.use_payment_plan;

  // Signed Success Screen
  if (signed) {
    return (
      <div className="min-h-screen bg-gradient-hero">
        <div className="bg-gradient-to-r from-yellow-400 to-amber-400 py-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900">RenewBee PowerPack</h1>
        </div>
        <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
          <Card className="border-0 shadow-xl">
            <CardContent className="pt-8 pb-8 text-center space-y-6">
              <div className="relative mx-auto w-20 h-20">
                <div className="absolute inset-0 rounded-full bg-success/20 animate-ping" />
                <div className="relative w-20 h-20 rounded-full bg-success flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-success-foreground" />
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-2">
                  {language === 'de' ? 'Vertrag erfolgreich unterschrieben!' : 'Contract Signed Successfully!'}
                </h2>
                <p className="text-muted-foreground">
                  {language === 'de'
                    ? 'Vielen Dank! Sie erhalten eine Kopie des unterschriebenen Vertrags per E-Mail.'
                    : 'Thank you! You will receive a copy of the signed contract by email.'}
                </p>
              </div>
              {signResult?.signed_at && (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 border text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{language === 'de' ? 'Unterzeichnet am' : 'Signed on'}: <strong>{new Date(signResult.signed_at).toLocaleString(locale)}</strong></span>
                </div>
              )}
              <Button onClick={handleDownloadPdf} variant="premium" size="lg">
                <Download className="w-5 h-5 mr-2" />
                {language === 'de' ? 'Unterschriebenen Vertrag herunterladen' : 'Download Signed Contract'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Signing Page
  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-400 to-amber-400 py-4 text-center">
        <h1 className="text-2xl font-bold text-gray-900">RenewBee PowerPack</h1>
        <p className="text-sm text-gray-700">{language === 'de' ? 'Kaufvertrag' : 'Sales Contract'}</p>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Contract Details */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {language === 'de' ? 'Vertragsdetails' : 'Contract Details'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Customer Number */}
            {contract.customer_number && (
              <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 font-medium">
                {language === 'de' ? 'Kundennummer' : 'Customer Number'}: {contract.customer_number}
              </div>
            )}

            {/* Customer Info */}
            <div className="p-4 rounded-lg bg-muted/30 border">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-4 h-4 text-primary" />
                <span className="font-medium text-sm">{language === 'de' ? 'Kundeninformationen' : 'Customer Information'}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground">{language === 'de' ? 'Name' : 'Name'}:</span> <strong>{customerInfo.name}</strong></div>
                {customerInfo.companyName && <div><span className="text-muted-foreground">{language === 'de' ? 'Firma' : 'Company'}:</span> <strong>{customerInfo.companyName}</strong></div>}
                <div><span className="text-muted-foreground">{language === 'de' ? 'E-Mail' : 'Email'}:</span> <strong>{customerInfo.email}</strong></div>
                <div><span className="text-muted-foreground">{language === 'de' ? 'Telefon' : 'Phone'}:</span> <strong>{customerInfo.phone}</strong></div>
                <div className="sm:col-span-2"><span className="text-muted-foreground">{language === 'de' ? 'Adresse' : 'Address'}:</span> <strong>{customerInfo.address}</strong></div>
              </div>
            </div>

            {/* Product Details */}
            <div className="p-4 rounded-lg bg-muted/30 border">
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-4 h-4 text-primary" />
                <span className="font-medium text-sm">{language === 'de' ? 'Produktdetails' : 'Product Details'}</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>RenewBee PowerPack x{productDetails.quantity}</span>
                  <span className="font-medium">{formatCurrency(productDetails.subtotal, locale)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{language === 'de' ? 'MwSt. (19%)' : 'VAT (19%)'}</span>
                  <span>{formatCurrency(productDetails.vat, locale)}</span>
                </div>
                <Separator />
                <div className="flex justify-between pt-1">
                  <span className="font-semibold">{language === 'de' ? 'Gesamtbetrag' : 'Total'}</span>
                  <span className="font-bold text-primary text-lg">{formatCurrency(productDetails.totalPrice, locale)}</span>
                </div>
              </div>
            </div>

            {/* Payment Plan */}
            <div className="p-4 rounded-lg bg-muted/30 border">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-4 h-4 text-primary" />
                <span className="font-medium text-sm">{language === 'de' ? 'Zahlungsart' : 'Payment Method'}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {usePaymentPlan
                  ? (language === 'de' ? 'Zahlungsplan: 50% bei Vertragsunterzeichnung / 25% bei Lieferung / 25% bei Installation' : 'Payment Plan: 50% at signing / 25% on delivery / 25% on installation')
                  : (language === 'de' ? 'Vollzahlung bei Vertragsunterzeichnung' : 'Full payment at contract signing')
                }
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Terms & Conditions */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="cursor-pointer" onClick={() => setTermsExpanded(!termsExpanded)}>
            <CardTitle className="flex items-center justify-between text-base">
              <span>{language === 'de' ? 'Allgemeine Geschäftsbedingungen' : 'Terms & Conditions'}</span>
              {termsExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          {termsExpanded && (
            <CardContent>
              <div className="max-h-64 overflow-y-auto text-xs text-muted-foreground space-y-3 p-3 bg-muted/20 rounded border">
                {t.terms?.sections && Object.values(t.terms.sections).map((section, idx) => (
                  <div key={idx}>
                    <h4 className="font-semibold text-foreground">{section.title}</h4>
                    <p className="whitespace-pre-line">{section.content}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
          <CardContent className={termsExpanded ? 'pt-0' : ''}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-muted-foreground">
                {language === 'de'
                  ? 'Ich habe die Allgemeinen Geschäftsbedingungen gelesen und akzeptiere sie.'
                  : 'I have read and accept the Terms & Conditions.'}
              </span>
            </label>
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5 text-primary" />
              {language === 'de' ? 'Digitale Unterschrift' : 'Digital Signature'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">{language === 'de' ? 'Name' : 'Name'}</Label>
                <p className="font-medium">{customerInfo.name}</p>
              </div>
              <div>
                <Label>{language === 'de' ? 'Ort' : 'Location'} *</Label>
                <Input
                  value={signatureLocation}
                  onChange={(e) => setSignatureLocation(e.target.value)}
                  placeholder={language === 'de' ? 'Stadt' : 'City'}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="mb-2 block">{language === 'de' ? 'Unterschrift zeichnen' : 'Draw Signature'} *</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white">
                <SignatureCanvas
                  ref={sigCanvasRef}
                  canvasProps={{
                    className: 'w-full',
                    style: { width: '100%', height: '200px' }
                  }}
                  backgroundColor="white"
                />
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={handleClearSignature} className="mt-2">
                {language === 'de' ? 'Unterschrift löschen' : 'Clear Signature'}
              </Button>
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>{language === 'de' ? 'Datum' : 'Date'}: {new Date().toLocaleDateString(locale)}</span>
            </div>

            <Button
              onClick={handleSign}
              disabled={signing || !termsAccepted || !signatureLocation.trim()}
              variant="premium"
              size="lg"
              className="w-full"
            >
              {signing ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {language === 'de' ? 'Wird unterschrieben...' : 'Signing...'}</>
              ) : (
                <><PenTool className="w-5 h-5 mr-2" /> {language === 'de' ? 'Vertrag unterschreiben' : 'Sign Contract'}</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground py-4">
          <p>RenewBee GmbH | Weserstraße 5, 12045 Berlin</p>
          <p>HRB 260515 B | DE369471524</p>
        </div>
      </div>
    </div>
  );
};

export default CustomerSigningPage;
